// ABOUTME: The design system's compact dialog — a centred card that asks one thing and closes
// ABOUTME: For side panels and full-screen editors use CModalCore instead

import { useCallback, useEffect, useId, useRef, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { OnTopProvider, useEscapeOwnership } from "./escapeStack"
import { useFocusTrap } from "@shared/hooks/useFocusTrap"
import { useZIndex } from "@shared/hooks/useZIndex"
import { CloseIcon } from "@shared/icons"
import { CButton } from "./CButton"

/**
 * Desktop widths the design draws, by what the dialog holds: a confirmation,
 * a short form, a list of choices. A phone always gets the 16px gutters.
 */
const WIDTH_CLASS = {
  420: "md:max-w-[420px]",
  440: "md:max-w-[440px]",
  460: "md:max-w-[460px]",
  480: "md:max-w-[480px]",
} as const

interface CModalDialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  /** Desktop width; below `md` the card is the screen minus its gutters. */
  width?: keyof typeof WIDTH_CLASS
  /** Submits on Enter and on the primary button; omit for a dialog with no form. */
  onSubmit?: () => void
  submitLabel?: string
  submitDisabled?: boolean
  isSubmitting?: boolean
  cancelLabel?: string
  /** Replaces the cancel/submit pair entirely. */
  footer?: ReactNode
  /** Omit when the title is the whole message — a confirmation, say. */
  children?: ReactNode
}

export const CModalDialog = ({
  isOpen,
  onClose,
  title,
  width = 440,
  onSubmit,
  submitLabel,
  submitDisabled = false,
  isSubmitting = false,
  cancelLabel,
  footer,
  children,
}: CModalDialogProps) => {
  const { t } = useTranslation()
  const zIndex = useZIndex(isOpen)
  const isTopmostOverlay = useEscapeOwnership(isOpen, zIndex)
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previous
    }
  }, [isOpen])

  // Focus belongs to the dialog while it is open: Tab must not walk out into
  // the page behind it, and whatever was focused before gets it back on close.
  // The panel is there once the dialog has its z-index (it renders nothing before).
  useFocusTrap(panelRef, isOpen && zIndex !== 0)

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      // A dropdown or autocomplete inside the dialog answers Escape first and
      // marks it; closing that must not also close the dialog around it.
      if (event.defaultPrevented) return
      // And a dialog buried under another overlay leaves the keypress to the one on top.
      if (!isTopmostOverlay()) return
      // A request is in flight; closing now would leave the user guessing whether
      // it went through.
      if (isSubmitting) return
      if (event.key === "Escape") onClose()
    },
    [onClose, isSubmitting, isTopmostOverlay],
  )

  useEffect(() => {
    if (!isOpen) return
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen, handleEscape])

  if (!isOpen || zIndex === 0) return null

  // A confirmation is a title and two buttons: no body, and then no hairline
  // under the header either — the mock draws it only over content.
  const hasBody = Boolean(children)

  const body = (
    <>
      {/* 18/20/14 header, 16/20/20 body, 14/20/18 footer — the paddings of the
          mock's dialog, shared by every screen that opens one. */}
      <div className={`flex items-start gap-3 px-5 pt-4.5 pb-3.5 ${hasBody ? "border-b border-border" : ""}`}>
        <h2
          id={titleId}
          className="min-w-0 flex-1 text-[17px] font-semibold tracking-[-0.01em] text-pretty break-words md:text-[18px]"
        >
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label={t("common.close")}
          className="-mt-1.5 -mr-1.5 flex size-9 flex-none items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 max-md:size-11"
        >
          <CloseIcon size={16} />
        </button>
      </div>

      {hasBody && <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-5">{children}</div>}

      {(footer ?? onSubmit) && (
        // On a phone the buttons stack and the primary one comes first, so the
        // thumb lands on it — cancel is pushed last by `order`.
        <div className="flex justify-end gap-2 border-t border-border px-5 pt-3.5 pb-4.5 max-md:flex-col">
          {footer ?? (
            <>
              <CButton
                type="button"
                color="gray"
                size="md"
                onClick={onClose}
                label={cancelLabel ?? t("common.cancel")}
                className="max-md:order-2 max-md:min-h-11 max-md:w-full"
                showLabelOnMobile
              />
              <CButton
                type="submit"
                color="blue"
                size="md"
                disabled={submitDisabled}
                isProcessing={isSubmitting}
                label={submitLabel ?? t("common.save")}
                className="font-semibold max-md:min-h-11 max-md:w-full"
                showLabelOnMobile
              />
            </>
          )}
        </div>
      )}
    </>
  )

  // Not a control, and deliberately so. This container once became a <button> to
  // satisfy the a11y lint about a click handler on a <div> — which put the whole
  // dialog, its own buttons included, inside a button: invalid nesting that React
  // warns about, and the card inherited the button's centred text. role="presentation"
  // is how CPopup answers the same rule.
  //
  // The backdrop does not close the dialog. It never did: the dim layer below covers
  // this container, so a click beside the card landed on the layer, and the handler
  // that looked for a click on the container itself could not fire. It stays that
  // way on purpose — the dialog holds forms, and a stray click must not throw their
  // input away. Close goes through the dialog's buttons and Escape.
  const dialog = (
    <div
      role="presentation"
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex }}
      onClick={(event) => {
        // A click inside the card ends here, as it used to end on the card itself:
        // through the portal it would otherwise reach whatever opened the dialog.
        if (event.target instanceof Node && panelRef.current?.contains(event.target)) event.stopPropagation()
      }}
    >
      <div className="fixed inset-0 bg-black/45" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative flex max-h-[calc(100dvh-32px)] w-[calc(100%-32px)] flex-col overflow-hidden rounded-xl bg-white shadow-dialogShadow duration-200 animate-in fade-in zoom-in-95 ${WIDTH_CLASS[width]}`}
      >
        {onSubmit ? (
          <form
            className="flex min-h-0 flex-col"
            onSubmit={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onSubmit()
            }}
          >
            {body}
          </form>
        ) : (
          body
        )}
      </div>
    </div>
  )

  // What the dialog shows learns whether another overlay covers it.
  const content = <OnTopProvider value={isTopmostOverlay}>{dialog}</OnTopProvider>
  const root = document.getElementById("modal")
  return root ? createPortal(content, root) : content
}
